import React from 'react';
import type { Placement } from '@floating-ui/react-dom';
import { useRightPanelWidth } from '../../app/layout/RightPanel';
import { useSidebarOpenState } from '../../app/layout/SidebarOpenStateContext';
import PopOverArrow from '../../components/PopOverArrow';
import PopOverPrimitive from '../../components/PopOverPrimitive';
import resizeOnShow from '../../utils/popover/resizeOnShow';
import { SIDEBAR_WIDTH } from '../PostLayoutSizes';
import * as styles from './ControlBarPopOver.module.scss';

const OFFSET = 14;
const SCREEN_PADDING = 12;

interface ControlBarPopOverProps {
  children: React.ReactNode;
  content: React.ReactNode;
  open: boolean;
  placement: Placement;
  onOpenChange: (open: boolean) => void;
  preventScreenOverflow?: boolean | { minHeight?: number };
  arrowClassName?: string;
  anchorRef?: React.ComponentProps<typeof PopOverPrimitive>['anchorRef'];
}

const ControlBarPopOver = ({
  content,
  open,
  children,
  placement,
  onOpenChange,
  arrowClassName,
  anchorRef,
  preventScreenOverflow,
}: ControlBarPopOverProps) => {
  const { isSidebarOpen } = useSidebarOpenState();
  const rightPanelWidth = useRightPanelWidth();

  return (
    <PopOverPrimitive
      onOpenChange={onOpenChange}
      open={open}
      offset={OFFSET}
      screenPadding={{
        top: SCREEN_PADDING,
        bottom: SCREEN_PADDING,
        left: SCREEN_PADDING + (isSidebarOpen ? SIDEBAR_WIDTH : 0),
        right: SCREEN_PADDING + rightPanelWidth,
      }}
      placement={placement}
      trigger="click"
      autoUpdateAnimationFrame
      noFlip
      anchorRef={anchorRef}
      getAdditionalMiddleware={
        preventScreenOverflow
          ? (options) => [
              resizeOnShow({
                padding: options.padding,
                minHeight:
                  typeof preventScreenOverflow === 'object'
                    ? preventScreenOverflow.minHeight
                    : undefined,
              }),
            ]
          : undefined
      }
    >
      <PopOverPrimitive.Trigger asChild>{children}</PopOverPrimitive.Trigger>
      <PopOverPrimitive.Floating className={styles.floatingContainer} noPortal>
        <PopOverPrimitive.Content className={styles.inner}>
          {content}
        </PopOverPrimitive.Content>
        <PopOverArrow className={arrowClassName} />
      </PopOverPrimitive.Floating>
    </PopOverPrimitive>
  );
};

ControlBarPopOver.Anchor = PopOverPrimitive.Anchor;

export default ControlBarPopOver;
